// utils/animation.tsx
// Centralized animation presets + simple API
// Use: getVariantFromSimpleControls(preset, duration)
// Presets: 'none' | 'fade' | 'slide' | 'zoom' | 'stagger'

import type { MotionProps } from 'framer-motion';
import { motion } from 'framer-motion';
import type {
    ComponentType,
    CSSProperties,
    HTMLAttributes,
    PropsWithChildren,
    ReactElement,
    ReactNode,
} from 'react';
import { useEffect, useMemo, useState } from 'react';

type PresetName = 'none' | 'fade' | 'slide' | 'zoom' | 'stagger';
type VariantState = Record<string, unknown>;
type VariantMap = Record<string, VariantState>;
type MotionDivProps = Omit<HTMLAttributes<HTMLDivElement>, keyof MotionProps> &
    MotionProps;
const MotionDiv = motion.div as ComponentType<
    PropsWithChildren<MotionDivProps>
>;

type TransitionConfig = {
    duration?: unknown;
    ease?: unknown;
    type?: unknown;
    delay?: unknown;
};

type PresetDefault = {
    presetOptions: Record<string, unknown> | null;
    transition: {
        ease: string;
        type: string;
        delay: number;
    };
};

interface UseMotionControlsOptions {
    preset?: string;
    duration?: number;
    editorPreview?: boolean;
    group?: string;
    keyBase?: string;
    viewportAmount?: number;
}

interface MotionControlsResult {
    enableAnim: boolean;
    isStagger: boolean;
    containerMotionProps: MotionDivProps;
    childMotionProps: MotionDivProps;
    enableContainer: (groupName: string) => boolean;
    enableChild: (groupName: string) => boolean;
    remountKey: string;
}

type DataAttributes = Record<`data-${string}`, string | number | boolean>;

interface MotionMaybeProps extends DataAttributes {
    enable: boolean;
    nodeKey?: string;
    motionProps?: MotionDivProps;
    className?: string;
    style?: CSSProperties;
    children?: ReactNode;
}

/* ------------------------------ helpers ------------------------------ */

const clamp = (n: number, lo: number, hi: number): number =>
    Math.max(lo, Math.min(n, hi));

// bias 0..1 toward 0 (p < 1 makes the quick end snappier)
const bias = (t: number, p = 0.58): number => Math.pow(clamp(t, 0, 1), p);

const isRecord = (value: unknown): value is Record<string, unknown> =>
    Boolean(value) && typeof value === 'object';

function hasPerPropTiming(transition: unknown): boolean {
    if (!isRecord(transition)) return false;

    return Object.keys(transition).some(
        (key) => !['duration', 'ease', 'delay', 'type'].includes(key)
    );
}

const getVariantStateTransition = (
    base: VariantMap,
    stateKey: string
): Record<string, unknown> | undefined => {
    const state = base[stateKey];
    if (!isRecord(state)) return undefined;
    const transition = state.transition;
    return isRecord(transition) ? transition : undefined;
};

/* ----------------------- editor defaults (root) ----------------------- */

export const PRESET_DEFAULTS: Record<PresetName, PresetDefault> = {
    none: {
        presetOptions: null,
        transition: { ease: 'easeIn', type: 'tween', delay: 0 },
    },
    fade: {
        presetOptions: {},
        transition: { ease: 'easeIn', type: 'tween', delay: 0.3 },
    },
    slide: {
        presetOptions: { direction: 'up', distance: 10 },
        transition: { ease: 'easeIn', type: 'tween', delay: 0.3 },
    },
    zoom: {
        presetOptions: { from: 0.985 },
        transition: { ease: 'easeInOut', type: 'tween', delay: 0.04 },
    },
    stagger: {
        presetOptions: {},
        transition: { ease: 'easeIn', type: 'tween', delay: 0.3 },
    },
};

/* ------------- derive durations from the editor's Duration ------------ */

function deriveDurations(dur: unknown): {
    opacityDur: number;
    axisDur: number;
    scaleTotal: number;
    delayO: number;
    delayA: number;
    staggerChildren: number;
    delayChildren: number;
    slideOpacityDur: number;
    slideAxisDur: number;
} {
    // Keep your established overall range. D feeds the general timings.
    const D = clamp(Number(dur) || 0.6, 0.1, 1.6);
    // Normalize to 0..1 then bias so the quick end (near 0) is snappier for SLIDE.
    const t = (D - 0.1) / (2.0 - 0.1);
    const tb = bias(t, 0.58); // smaller => even snappier quick end

    // General timings (used by fade/zoom/stagger, and baseline slide if needed)
    const opacityDur = clamp(0.15 + 0.85 * D, 0.1, 2);
    const axisDur = clamp(0.35 + 0.55 * D, 0.2, 1.8);
    const scaleTotal = clamp(0.4 + 0.8 * D, 0.25, 2.0);

    // Slide-specific quick-biased timings:
    // Keep the SAME slowest (max) as before; lower the mins and shape with bias.
    const slideOpacityMin = 0;
    const slideOpacityMax = 1.2;
    const slideAxisMin = 0;
    const slideAxisMax = 1; // unchanged slow end

    const slideOpacityDur =
        slideOpacityMin + (slideOpacityMax - slideOpacityMin) * tb;
    const slideAxisDur = slideAxisMin + (slideAxisMax - slideAxisMin) * tb;

    return {
        opacityDur,
        axisDur,
        scaleTotal,
        delayO: 0.06,
        delayA: 0.1,
        staggerChildren: clamp(0.08 + 0.25 * D, 0.06, 0.5),
        delayChildren: clamp(0.06 + 0.2 * D, 0.04, 0.4),
        slideOpacityDur,
        slideAxisDur,
    };
}

/* ---------------------------- preset factories ---------------------------- */

const PRESETS: Record<
    PresetName,
    (opts?: Record<string, unknown>, dur?: unknown) => VariantMap | null
> = {
    none: () => null,

    // Duration-aware fade
    fade: (_opts = {}, dur = 0.6) => {
        void _opts;
        const { opacityDur } = deriveDurations(dur);
        return {
            offscreen: { opacity: 0 },
            onscreen: {
                opacity: 1,
                transition: { opacity: { duration: opacityDur } },
            },
        };
    },

    // Slide with quick-biased curve at low Duration, same slowest cap
    slide: (opts = {}, dur = 0.6) => {
        const direction = String(opts.direction ?? 'up');
        const distance = Number(opts.distance ?? 30);

        const { slideOpacityDur, slideAxisDur, delayO, delayA } =
            deriveDurations(dur);

        const isHorizontal = direction === 'left' || direction === 'right';
        const axis = isHorizontal ? 'x' : 'y';
        const sign =
            direction === 'up'
                ? +1
                : direction === 'down'
                ? -1
                : direction === 'left'
                ? -1
                : +1;

        return {
            offscreen: { [axis]: sign * distance, opacity: 0 },
            onscreen: {
                [axis]: 0,
                opacity: 1,
                transition: {
                    opacity: { duration: slideOpacityDur, delay: delayO },
                    [axis]: { duration: slideAxisDur, delay: delayA },
                },
            },
        };
    },

    // Zoom where the total duration scales with Duration
    zoom: (opts = {}, dur = 0.6) => {
        const from = Number(opts.from ?? 0.98);

        // Editor Duration D ∈ [0.1, 1.6]
        const D = clamp(Number(dur) || 0.6, 0.1, 2);

        // Normalize to 0..1
        const t = (D - 0.1) / (1.6 - 0.1);

        // Overall time: linear to reach a *much* slower top end (≈1.6s)
        const total = 0.22 + (1.6 - 0.22) * t; // 0.22 .. 1.60

        // Keep the *feel* snappy at low end for individual props
        // (quick opacity, quick blur clear), but let total scale up heavily.
        const opacityT = Math.max(0.12, Math.min(total * 0.55, 0.9));
        const moveT = Math.max(0.16, total * 0.5);
        const blurT = Math.max(0.18, total * 0.55);

        // Small overshoot for life without bounciness
        const settleMag = 0.012; // ~1.2%

        return {
            offscreen: { scale: from, opacity: 0, y: 6, filter: 'blur(.5px)' },
            onscreen: {
                scale: [from, 1 + settleMag, 1],
                opacity: [0, 1],
                y: [6, 0, 0],
                filter: ['blur(.5px)', 'blur(0px)'],
                transition: {
                    // Spring drives the *feel*; duration caps the overall tempo
                    scale: {
                        type: 'spring',
                        stiffness: 560,
                        damping: 34,
                        mass: 0.9,
                        duration: total,
                    },
                    opacity: { duration: opacityT, ease: 'easeOut' },
                    y: { duration: moveT, ease: 'easeOut' },
                    filter: { duration: blurT, ease: 'easeOut' },
                    delay: 0.04,
                },
            },
        };
    },

    // Container-only; children should use fade/slide/etc.
    stagger: (_opts = {}, dur = 0.6) => {
        void _opts;
        const { staggerChildren, delayChildren } = deriveDurations(dur);
        return {
            hidden: {},
            show: {
                transition: { staggerChildren, delayChildren },
            },
        };
    },
};

/* ----------------------- root transition merge logic ---------------------- */

function buildTransition({
    duration = 0.6,
    ease = 'easeOut',
    type = 'tween',
    delay = 0,
}: TransitionConfig = {}): {
    duration: number;
    ease: unknown;
    type: unknown;
    delay: number;
} {
    const D = clamp(Number(duration) || 0.6, 0.1, 5);
    return { duration: D, ease, type, delay: Math.max(0, Number(delay) || 0) };
}

export function normalizeAnimationPreset(
    value: unknown,
    group = 'content'
): string {
    const map: Record<string, string> = {
        '': 'slide',
        none: 'none',
        fade: 'fade',
        'fade in': 'fade',
        slide: 'slide',
        'slide up': 'slide',
        zoom: 'zoom',
        'zoom in': 'zoom',
        stagger: group === 'features' ? 'stagger' : 'fade',
        cascade: group === 'features' ? 'stagger' : 'fade',
    };

    const key = String(value ?? '')
        .toLowerCase()
        .trim();

    return map[key] ?? 'slide';
}

function normalizePreset(preset: string): PresetName {
    return (
        Object.prototype.hasOwnProperty.call(PRESETS, preset) ? preset : 'fade'
    ) as PresetName;
}

export function getVariantFromSimpleControls(
    preset = 'fade',
    duration = 0.6
): VariantMap | null {
    const selectedPreset = normalizePreset(preset);
    const factory = PRESETS[selectedPreset];
    const defaults = PRESET_DEFAULTS[selectedPreset];

    // pass duration into factory so per-prop timings reflect editor changes
    const base = factory(defaults.presetOptions ?? {}, duration);
    if (!base) return null;

    // merge preset’s root transition (incl. delay/ease) with duration-aware root
    const root = buildTransition({
        ...defaults.transition,
        duration,
    });

    // If the preset defines per-property timings, we avoid overriding their durations
    const stateKey = Object.prototype.hasOwnProperty.call(base, 'show')
        ? 'show'
        : 'onscreen';
    const existing = getVariantStateTransition(base, stateKey);

    const mergedRoot = hasPerPropTiming(existing)
        ? Object.fromEntries(
              Object.entries(root).filter(([key]) => key !== 'duration')
          )
        : root;

    if ('hidden' in base || 'show' in base) {
        return {
            hidden: base.hidden ?? {},
            show: {
                ...(isRecord(base.show) ? base.show : {}),
                transition: { ...(existing || {}), ...mergedRoot },
            },
        };
    }

    return {
        offscreen: base.offscreen ?? {},
        onscreen: {
            ...(isRecord(base.onscreen) ? base.onscreen : {}),
            transition: { ...(existing || {}), ...mergedRoot },
        },
    };
}

/* ---------------------- Hook + lightweight wrapper ---------------------- */

export function useMotionControls({
    preset = 'fade',
    duration = 0.6,
    editorPreview = false,
    group = 'content',
    keyBase = '',
    viewportAmount = 0.5,
}: UseMotionControlsOptions = {}): MotionControlsResult {
    const [prefersReduced, setPrefersReduced] = useState(false);

    useEffect(() => {
        if (typeof window !== 'undefined' && window.matchMedia) {
            const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
            const onChange = (event: MediaQueryListEvent) =>
                setPrefersReduced(event.matches);

            setPrefersReduced(mq.matches);
            mq.addEventListener?.('change', onChange);
            return () => mq.removeEventListener?.('change', onChange);
        }

        return undefined;
    }, []);

    const enableAnim = !prefersReduced && preset !== 'none';
    const isStagger = preset === 'stagger';
    const viewport = useMemo(
        () => ({ once: !editorPreview, amount: viewportAmount }),
        [editorPreview, viewportAmount]
    );

    const remountKey = useMemo(
        () =>
            `${keyBase || 'anim'}-${preset}-${String(duration)}-${group}-${
                editorPreview ? 'preview' : 'live'
            }`,
        [keyBase, preset, duration, group, editorPreview]
    );

    const containerVariants = useMemo(
        () =>
            isStagger
                ? getVariantFromSimpleControls('stagger', duration)
                : null,
        [isStagger, duration]
    );

    const childVariants = useMemo(
        () =>
            getVariantFromSimpleControls(isStagger ? 'fade' : preset, duration),
        [isStagger, preset, duration]
    );

    const containerMotionProps = useMemo<MotionDivProps>(() => {
        if (!enableAnim || !isStagger || !containerVariants) return {};

        return editorPreview
            ? {
                  variants: containerVariants,
                  initial: 'hidden',
                  animate: 'show',
              }
            : {
                  variants: containerVariants,
                  initial: 'hidden',
                  whileInView: 'show',
                  viewport,
              };
    }, [enableAnim, isStagger, containerVariants, editorPreview, viewport]);

    const childMotionProps = useMemo<MotionDivProps>(() => {
        if (!enableAnim || !childVariants) return {};

        const hasHiddenShow =
            Object.prototype.hasOwnProperty.call(childVariants, 'hidden') ||
            Object.prototype.hasOwnProperty.call(childVariants, 'show');

        return editorPreview
            ? {
                  variants: childVariants,
                  initial: hasHiddenShow ? 'hidden' : 'offscreen',
                  animate: hasHiddenShow ? 'show' : 'onscreen',
              }
            : {
                  variants: childVariants,
                  initial: hasHiddenShow ? 'hidden' : 'offscreen',
                  whileInView: hasHiddenShow ? 'show' : 'onscreen',
                  viewport,
              };
    }, [enableAnim, childVariants, editorPreview, viewport]);

    const enableContainer = (groupName: string) =>
        enableAnim && isStagger && groupName === group;
    const enableChild = (groupName: string) =>
        enableAnim && groupName === group;

    return {
        enableAnim,
        isStagger,
        containerMotionProps,
        childMotionProps,
        enableContainer,
        enableChild,
        remountKey,
    };
}

export function MotionMaybe({
    enable,
    nodeKey,
    motionProps,
    className,
    style,
    children,
    ...rest
}: MotionMaybeProps): ReactElement {
    if (enable) {
        return (
            <MotionDiv
                key={nodeKey}
                className={className}
                style={style}
                {...rest}
                {...(motionProps || {})}
            >
                {children}
            </MotionDiv>
        );
    }

    return (
        <div key={nodeKey} className={className} style={style} {...rest}>
            {children}
        </div>
    );
}
